import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Fereastra modala a lui Bootstrap 3, cu markupul din machete si gesturile
 * din bootstrap.js: `body.modal-open`, `.modal-backdrop.fade.in` pus pe body,
 * `.modal.fade` care primeste `display:block` si apoi `in` (tranzitie 300 ms),
 * si drumul invers la inchidere. `staticBackdrop` si `keyboard` sunt
 * data-backdrop="static" / data-keyboard="false" de pe element.
 */
export function Modal({ id, open, onClose, title, labelledBy = 'myModalLabel', staticBackdrop = false, keyboard = true, large = false, portal = false, footer, children }: {
  id: string
  open: boolean
  onClose(): void
  title: ReactNode
  labelledBy?: string
  staticBackdrop?: boolean
  keyboard?: boolean
  /** `modal-lg`: fereastra lata din machete (sabloanele de email, de pilda). */
  large?: boolean
  /**
   * Randeaza fereastra prin portal in `document.body`, ca backdrop-ul (care e
   * deja pe body). Implicit false, ca sa nu schimbe comportamentul (si testele)
   * modalelor existente. Se pune `portal` cand modalul e deschis din interiorul
   * unui context de stacking (ex. un rand expandat de DataTable cu transform):
   * altfel fereastra ramane prinsa sub backdrop-ul gri si nu se poate apasa.
   */
  portal?: boolean
  /** `.modal-footer`: butoanele de sub corp. Lipseste in machetele fara ele. */
  footer?: ReactNode
  children: ReactNode
}) {
  /* Fazele din bootstrap.js: display:block, apoi `in` (300 ms); la inchidere
     `in` cade si abia dupa 300 ms dispare elementul. */
  const [phase, setPhase] = useState<'hidden' | 'in' | 'leaving'>(open ? 'in' : 'hidden')
  /* S-a deschis vreodata? Vezi efectul de mai jos. */
  const opened = useRef(open)
  const shown = open || phase !== 'hidden'
  const inClass = open && phase === 'in'

  useEffect(() => {
    if (open) {
      opened.current = true
      const frame = requestAnimationFrame(() => setPhase('in'))
      return () => cancelAnimationFrame(frame)
    }
    /*
     * O fereastra care nu s-a deschis NICIODATA n-are ce inchide. Fara randul
     * asta, efectul de mai jos ruleaza si la asezarea in pagina: `phase` trece
     * pe `leaving`, `shown` devine adevarat, si o fereastra pe care n-a cerut-o
     * nimeni primeste `display:block` si isi pune valul peste pagina pentru
     * 300 ms, la fiecare desenare a ecranului care o tine. Se vedea de cand
     * lista Managed a capatat fereastra de repornire: valul clipea la incarcare.
     */
    if (!opened.current) return
    const frame = requestAnimationFrame(() => setPhase('leaving'))
    const timer = setTimeout(() => setPhase('hidden'), 300)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    if (!shown) return
    document.body.classList.add('modal-open')
    const backdrop = document.createElement('div')
    backdrop.className = 'modal-backdrop fade'
    document.body.appendChild(backdrop)
    const frame = requestAnimationFrame(() => backdrop.classList.add('in'))
    return () => {
      cancelAnimationFrame(frame)
      backdrop.classList.remove('in')
      setTimeout(() => backdrop.remove(), 150)
      document.body.classList.remove('modal-open')
    }
  }, [shown])

  useEffect(() => {
    if (!open || !keyboard) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, keyboard, onClose])

  const dialog = (
    <div
      className={`modal fade${inClass ? ' in' : ''}`}
      id={id}
      tabIndex={-1}
      role="dialog"
      aria-labelledby={labelledBy}
      aria-hidden={shown ? undefined : 'true'}
      style={shown ? { display: 'block' } : undefined}
      onClick={(ev) => {
        if (!staticBackdrop && ev.target === ev.currentTarget) onClose()
      }}
    >
      <div className={`modal-dialog${large ? ' modal-lg' : ''}`} role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="modal-title" id={labelledBy}>
              {title}
            </h4>
          </div>
          <div className="modal-body">{children}</div>
          {footer !== undefined && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  )

  return portal ? createPortal(dialog, document.body) : dialog
}
